import { useEffect, useRef } from "react";
import { BarChart3, FolderLock, HardDrive, Image, Images } from "lucide-react";

type Props = {
  open: boolean;
  onClose: () => void;
  onPickGallery: () => void;
  onPickDevice: () => void;
  onPickCoffre: () => void;
  onPickPhotos: () => void;
  onPickPoll?: () => void;
};

const PHOTO_OPTIONS = [
  {
    id: "gallery" as const,
    title: "Galerie",
    hint: "Photos et images de l’appareil",
    Icon: Images,
  },
];

const FILE_OPTIONS = [
  {
    id: "device" as const,
    title: "Cet appareil",
    hint: "PDF, documents et tous fichiers",
    Icon: HardDrive,
  },
  {
    id: "coffre" as const,
    title: "Coffre",
    hint: "Espace sécurisé et GED",
    Icon: FolderLock,
  },
  {
    id: "photos" as const,
    title: "Photos",
    hint: "Bibliothèque Aurélia",
    Icon: Image,
  },
];

export function ChatAttachSourceModal({
  open,
  onClose,
  onPickGallery,
  onPickDevice,
  onPickCoffre,
  onPickPhotos,
  onPickPoll,
}: Props) {
  const openedAt = useRef(0);

  useEffect(() => {
    if (!open) return;
    openedAt.current = Date.now();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const pickFile = (id: "device" | "coffre" | "photos") => {
    if (id === "device") onPickDevice();
    else if (id === "coffre") onPickCoffre();
    else onPickPhotos();
  };

  return (
    <div
      className="chat-confirm-overlay fixed inset-0 z-[100] flex items-end justify-center p-3 sm:items-center sm:p-4"
      role="presentation"
      onClick={(e) => {
        if (Date.now() - openedAt.current < 480) return;
        if (e.target === e.currentTarget) onClose();
      }}
      onPointerDown={(e) => {
        if (Date.now() - openedAt.current < 480) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      <div
        className="chat-confirm-dialog w-full max-w-md overflow-hidden rounded-2xl border border-[var(--chat-border)] bg-[var(--chat-surface)] shadow-[var(--chat-shadow)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="chat-attach-source-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-[var(--chat-border)] px-4 py-3">
          <h2 id="chat-attach-source-title" className="text-sm font-semibold text-[var(--chat-text)]">
            Ajouter
          </h2>
        </div>

        <div className="p-2">
          <p className="px-3 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--chat-muted)]">
            Photo
          </p>
          <ul>
            {PHOTO_OPTIONS.map(({ id, title, hint, Icon }) => (
              <li key={id}>
                <button
                  type="button"
                  className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left hover:bg-[var(--chat-surface-elevated)]"
                  onClick={() => onPickGallery()}
                >
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[var(--chat-accent-soft)] text-[var(--chat-accent-strong)]">
                    <Icon className="size-4" aria-hidden />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-[var(--chat-text)]">{title}</span>
                    <span className="block text-[11px] text-[var(--chat-muted)]">{hint}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>

          <div className="mx-3 my-2 border-t border-[var(--chat-border)]" />
          <p className="px-3 pb-1 pt-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--chat-muted)]">
            Fichier
          </p>
          <ul>
            {FILE_OPTIONS.map(({ id, title, hint, Icon }) => (
              <li key={id}>
                <button
                  type="button"
                  className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left hover:bg-[var(--chat-surface-elevated)]"
                  onClick={() => pickFile(id)}
                >
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[var(--chat-accent-soft)] text-[var(--chat-accent-strong)]">
                    <Icon className="size-4" aria-hidden />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-[var(--chat-text)]">{title}</span>
                    <span className="block text-[11px] text-[var(--chat-muted)]">{hint}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>

          {onPickPoll ? (
            <>
              <div className="mx-3 my-2 border-t border-[var(--chat-border)]" />
              <p className="px-3 pb-1 pt-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--chat-muted)]">
                Conversation
              </p>
              <button
                type="button"
                className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left hover:bg-[var(--chat-surface-elevated)]"
                onClick={() => onPickPoll()}
              >
                <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-[var(--chat-accent-soft)] text-[var(--chat-accent-strong)]">
                  <BarChart3 className="size-4" aria-hidden />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-[var(--chat-text)]">Sondage</span>
                  <span className="block text-[11px] text-[var(--chat-muted)]">
                    Question + réponses, un clic pour voter
                  </span>
                </span>
              </button>
            </>
          ) : null}
        </div>

        <div className="flex justify-end border-t border-[var(--chat-border)] px-4 py-2.5">
          <button
            type="button"
            className="rounded-xl px-4 py-2 text-sm font-medium text-[var(--chat-muted)] hover:bg-[var(--chat-surface-elevated)]"
            onClick={onClose}
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
}
